import { useState } from 'react'
import { inject, observer } from 'mobx-react'
import PropTypes from 'prop-types'

import { Drawer } from '../../../../components'
import { withRouterProps } from '../../../../utils'
import { trackInvestmentsByTaxType } from '../../utils'
import EditInvestment from '../EditInvestment'
import InvestmentGroup from './InvestmentGroup'
import { GroupList } from './InvestmentRowStyle'

/**
 * Displays the account's investments, grouped by tax type when applicable.
 *
 * @param {object} props
 * @param {boolean} [props.showSplitBalanceWarning] Warns on the pre-tax group that a linked
 *   account's balances still need splitting between tax types.
 */
const InvestmentsDisplay = props => {
  const { type, accountType, id } = props.match.params
  const {
    features: { editInstitutionalAccount },
  } = props.store
  const {
    investedInvestments,
    totalBalance,
    preTaxInvestments,
    rothInvestments,
    afterTaxInvestments,
    notInvestedInvestments,
    preTaxBalance,
    rothBalance,
    afterTaxBalance,
  } = props.account
  const canEdit = type !== 'institutional' || editInstitutionalAccount
  const tracksInvestmentsByTaxType = trackInvestmentsByTaxType(accountType)
  const [editInvestment, setEditInvestment] = useState(null)

  const toggleEdit = (id, event) => {
    event?.stopPropagation()
    setEditInvestment(id)
  }

  return (
    <div>
      <GroupList>
        {tracksInvestmentsByTaxType ? (
          <>
            {preTaxInvestments.length > 0 && (
              <InvestmentGroup
                balance={preTaxBalance}
                balanceLabel='Pre-tax balance'
                investments={preTaxInvestments}
                title='Pre-tax investments'
                accountId={id}
                canEdit={canEdit}
                toggleEdit={toggleEdit}
                showSplitBalanceWarning={props.showSplitBalanceWarning}
              />
            )}

            {rothInvestments.length > 0 && (
              <InvestmentGroup
                balance={rothBalance}
                balanceLabel='Roth balance'
                investments={rothInvestments}
                title='Roth investments'
                accountId={id}
                canEdit={canEdit}
                toggleEdit={toggleEdit}
              />
            )}

            {afterTaxInvestments.length > 0 && (
              <InvestmentGroup
                balance={afterTaxBalance}
                balanceLabel='After-tax balance'
                investments={afterTaxInvestments}
                title='After-tax investments'
                accountId={id}
                canEdit={canEdit}
                toggleEdit={toggleEdit}
              />
            )}
          </>
        ) : (
          investedInvestments.length > 0 && (
            <InvestmentGroup
              investments={investedInvestments}
              balance={totalBalance}
              accountId={id}
              canEdit={canEdit}
              toggleEdit={toggleEdit}
            />
          )
        )}
        {notInvestedInvestments.length > 0 && tracksInvestmentsByTaxType && (
          <InvestmentGroup
            accountId={id}
            hideBalance
            investments={notInvestedInvestments}
            title='Other available investment options'
          />
        )}
      </GroupList>

      <Drawer
        active={Boolean(editInvestment)}
        title='Edit investment'
        maxWidth='700px'
        width='60%'
        responsiveWidth
        onOverlayClick={() => toggleEdit(null)}
        onEscKeyDown={() => toggleEdit(null)}>
        {editInvestment && <EditInvestment investmentId={editInvestment} toggleEdit={toggleEdit} />}
      </Drawer>
    </div>
  )
}

InvestmentsDisplay.propTypes = {
  showSplitBalanceWarning: PropTypes.bool,
}

export default withRouterProps(inject('store', 'account')(observer(InvestmentsDisplay)))
